import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowDownTrayIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReportTemplate from '../../components/reports/ReportTemplate';
import { ROUTES } from '../../consts/general';
import { RETURNS_PROCESSING } from '../../consts/reports';

const formatMinutes = (minutes) => {
  if (minutes == null) return '—';
  const total = Math.max(0, Number(minutes));
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  return `${days ? `${days}d ` : ''}${hours}h`;
};
const formatLabel = (value) => (
  value
    ? String(value).replace(/_/g, ' ').replace(/^\w/, (letter) => letter.toUpperCase())
    : '—'
);
const formatDueStatus = (value) => ({
  not_due: 'Not due',
  due_today: 'Due today',
  overdue: 'Overdue',
  on_time: 'On time',
  late: 'Late',
}[value] || '—');
const formatDesktopTimestamp = (value) => (value ? String(value).replace(/ PT$/, '') : '—');
const formatStatusTimestamp = (value) => (
  value
    ? new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value))
    : '—'
);
const STATE_OPTIONS = [
  ['all', 'All states'],
  ['scanned', 'Scanned in'],
  ['issued', 'Issued'],
  ['waiting', 'Waiting'],
  ['processed', 'Processed'],
  ['canceled', 'Canceled'],
  ['unmatched', 'Unmatched'],
  ['processed_before_scan', 'Processed before scan'],
];
const DUE_STATUS_OPTIONS = [
  ['all', 'All due statuses'],
  ['not_due', 'Not due'],
  ['due_today', 'Due today'],
  ['overdue', 'Overdue'],
  ['on_time', 'On time'],
  ['late', 'Late'],
];
const MOBILE_TOP_NAV_HEIGHT = 56;

const ReturnsProcessingReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [tableHeaderTop, setTableHeaderTop] = useState(0);
  const [filters, setFilters] = useState({
    from: '',
    to: '',
    state: 'all',
    dueStatus: 'all',
    warrantyOnly: false,
    search: '',
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const headerContentRef = useRef(null);
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearch(filters.search.trim()), 300);
    return () => clearTimeout(timeoutId);
  }, [filters.search]);
  const load = useCallback(async (source) => {
    try {
      setError('');
      const { data: result } = await axios.get('/api/v1/reports/returns-processing', {
        params: {
          from: filters.from || undefined,
          to: filters.to || undefined,
          state: filters.state,
          dueStatus: filters.dueStatus,
          warrantyOnly: filters.warrantyOnly ? 'true' : 'false',
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
        cancelToken: source?.token,
      });
      setData(result);
    } catch (err) {
      if (!axios.isCancel(err)) setError(err?.response?.data?.error || 'Could not load returns.');
    }
  }, [debouncedSearch, filters.dueStatus, filters.from, filters.state, filters.to, filters.warrantyOnly]);
  useEffect(() => {
    const source = axios.CancelToken.source();
    load(source);
    return () => source.cancel();
  }, [load]);
  useEffect(() => {
    const stickyHeader = headerContentRef.current?.closest('.sticky.z-10');
    if (!stickyHeader) return undefined;

    const updateTableHeaderTop = () => {
      const mobileTopOffset = window.matchMedia('(max-width: 767px)').matches ? MOBILE_TOP_NAV_HEIGHT : 0;
      setTableHeaderTop(stickyHeader.getBoundingClientRect().height + mobileTopOffset);
    };
    updateTableHeaderTop();
    const resizeObserver = new ResizeObserver(updateTableHeaderTop);
    resizeObserver.observe(stickyHeader);
    window.addEventListener('resize', updateTableHeaderTop);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateTableHeaderTop);
    };
  }, []);
  const handleExportCSV = async () => {
    try {
      setError('');
      setIsExporting(true);
      const { data: file } = await axios.get('/api/v1/reports/returns-processing/export', {
        responseType: 'blob',
        params: {
          ...(filters.from ? { from: filters.from } : {}),
          ...(filters.to ? { to: filters.to } : {}),
          state: filters.state,
          dueStatus: filters.dueStatus,
          warrantyOnly: String(filters.warrantyOnly),
          ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
        },
      });
      const url = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'returns-processing.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (!axios.isCancel(err)) setError(err?.response?.data?.error || 'Could not export returns.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportButton = (
    <button type="button" aria-label="Export CSV" onClick={handleExportCSV} disabled={isExporting} className="flex items-center gap-1.5 rounded-md bg-black px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-40">
      <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
      <span className="hidden md:inline">{isExporting ? 'Exporting…' : 'Export CSV'}</span>
    </button>
  );
  const reportFilters = (
    <div className="flex flex-wrap items-center gap-2">
      <input
        aria-label="Search"
        type="search"
        placeholder="Search order, customer, RMA, tracking"
        value={filters.search}
        onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
        className="h-8 w-full rounded-md border border-gray-300 px-2 text-sm md:w-64"
      />
      <input
        aria-label="Received from"
        type="date"
        value={filters.from}
        onChange={(event) => setFilters((current) => ({ ...current, from: event.target.value }))}
        className="hidden h-8 rounded-md border border-gray-300 px-2 text-sm md:inline-flex"
      />
      <span aria-hidden="true" className="hidden text-gray-500 md:inline-flex">–</span>
      <input
        aria-label="Through"
        type="date"
        value={filters.to}
        onChange={(event) => setFilters((current) => ({ ...current, to: event.target.value }))}
        className="hidden h-8 rounded-md border border-gray-300 px-2 text-sm md:inline-flex"
      />
      <select
        aria-label="State"
        value={filters.state}
        onChange={(event) => setFilters((current) => ({ ...current, state: event.target.value }))}
        className="h-8 rounded-md border border-gray-300 px-2 text-sm"
      >
        {STATE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>
      <select
        aria-label="Due status"
        value={filters.dueStatus}
        onChange={(event) => setFilters((current) => ({ ...current, dueStatus: event.target.value }))}
        className="hidden h-8 rounded-md border border-gray-300 px-2 text-sm md:inline-flex"
      >
        {DUE_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>
      <label className="hidden items-center gap-1.5 whitespace-nowrap text-sm text-gray-700 md:flex">
        <input
          type="checkbox"
          checked={filters.warrantyOnly}
          onChange={(event) => setFilters((current) => ({ ...current, warrantyOnly: event.target.checked }))}
          className="h-4 w-4"
        />
        Warranty only
      </label>
    </div>
  );
  const summary = data?.summary || {};
  const rows = data?.rows || [];
  const summaryItems = [
    { label: 'Scanned', value: summary.scannedInRange ?? '—' },
    { label: 'Waiting', value: summary.waiting ?? '—' },
    {
      label: 'Overdue',
      value: summary.overdue ?? '—',
      valueClassName: Number(summary.overdue) > 0 ? 'text-red-700' : 'text-gray-900',
    },
    { label: 'Due today', value: summary.dueToday ?? '—' },
    { label: 'Warranty', value: summary.warrantyWaiting ?? '—' },
    { label: 'Unmatched', value: summary.unmatchedExceptions ?? '—' },
    { label: 'Not received', value: summary.issuedNotReceived ?? '—' },
    { label: 'Canceled', value: summary.canceled ?? '—', hideBelowMd: true },
    { label: 'Processed', value: summary.processedInRange ?? '—' },
    { label: 'Avg', value: formatMinutes(summary.averageTurnaroundMinutes), hideBelowMd: true },
    { label: 'Median', value: formatMinutes(summary.medianTurnaroundMinutes), hideBelowMd: true },
  ];

  return (
    <ReportTemplate
      reportId={RETURNS_PROCESSING}
      title="Returns Processing"
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={!data && !error}
      error={error}
      exportButton={exportButton}
      headerContent={(
        <div>
          {reportFilters}
          <div ref={headerContentRef} className="mt-2 text-[11px] leading-4 text-gray-600 md:text-[13px] md:leading-5">
            <span className={data?.stale ? 'font-semibold text-red-700' : 'text-gray-600'}>
              Shopify as of {data?.lastSyncedAt ? `${formatStatusTimestamp(data.lastSyncedAt)} PT` : '—'}{data?.stale ? ' · stale' : ''}
            </span>
            {summaryItems.map(({ label, value, valueClassName, hideBelowMd }) => {
              const responsiveClass = hideBelowMd ? 'hidden md:inline' : '';
              return (
                <React.Fragment key={label}>
                  <span aria-hidden="true" className={responsiveClass}> · </span>
                  <span className={responsiveClass}>{label} <span className={`font-semibold ${valueClassName || 'text-gray-900'}`}>{value}</span></span>
                </React.Fragment>
              );
            })}
            <span aria-hidden="true"> · </span>
            <span>All times PT</span>
          </div>
        </div>
      )}
      renderChildrenOnly
    >
      <div className="space-y-4 overflow-x-clip">
        <div className="hidden rounded-lg border border-gray-200 bg-white md:block">
          <table className="table-fixed w-full text-left text-sm">
            <colgroup>
              <col style={{ width: '21%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '14%' }} />
            </colgroup>
            <thead
              className="sticky z-[1] bg-gray-50 text-xs uppercase tracking-wide text-gray-500"
              style={{ top: `${tableHeaderTop}px` }}
            >
              <tr>
                {['Tracking', 'RMA / Order', 'State', 'Received', 'Due', 'Processed', 'Outcome / Label'].map((heading) => (
                  <th key={heading} className="whitespace-nowrap px-1.5 py-2">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className={row.state === 'unmatched' || (row.is_warranty && row.state === 'waiting') ? 'bg-amber-50/60' : 'bg-white'}
                >
                  <td className="px-1.5 py-2 align-top">
                    <div className="truncate font-mono text-[13px] text-gray-900" title={row.tracking_number || ''}>{row.tracking_number || '—'}</div>
                    <div className="truncate text-xs text-gray-500" title={row.carrier || ''}>{row.carrier || '—'}</div>
                  </td>
                  <td className="px-1.5 py-2 align-top">
                    <div className="truncate text-[13px] text-gray-900" title={row.rma || ''}>{row.rma || '—'}</div>
                    <div
                      className="truncate text-xs text-gray-500"
                      title={`Order ${row.order_number || '—'}${row.customer_name ? ` · ${row.customer_name}` : ''}`}
                    >
                      Order {row.order_number || '—'}{row.customer_name ? ` · ${row.customer_name}` : ''}
                    </div>
                  </td>
                  <td className="px-1.5 py-2 align-top">
                    <div className="truncate text-sm text-gray-900">{formatLabel(row.state)}</div>
                    <div className="text-xs text-gray-500">
                      {row.is_warranty ? (
                        <span className="inline-flex rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-800">Warranty</span>
                      ) : <span aria-hidden="true">&nbsp;</span>}
                    </div>
                  </td>
                  <td className="px-1.5 py-2 align-top">
                    <div className="truncate text-[13px] text-gray-900" title={row.received_display || ''}>{formatDesktopTimestamp(row.received_display)}</div>
                    <div className="truncate text-xs text-gray-500" title={row.receivedBy?.full_name || ''}>{row.receivedBy?.full_name || '—'}</div>
                  </td>
                  <td className="px-1.5 py-2 align-top">
                    <div className="truncate text-[13px] text-gray-900" title={row.due_display || ''}>{row.due_display || '—'}</div>
                    <div className={`truncate text-xs ${row.due_status === 'overdue' ? 'text-red-700' : 'text-gray-500'}`}>
                      {formatDueStatus(row.due_status)}
                    </div>
                  </td>
                  <td className="px-1.5 py-2 align-top">
                    <div className="truncate text-[13px] text-gray-900" title={row.processed_display || ''}>{formatDesktopTimestamp(row.processed_display)}</div>
                    <div className="truncate text-xs text-gray-500" title={row.time_to_process || ''}>{row.time_to_process || '—'}</div>
                  </td>
                  <td className="px-1.5 py-2 align-top">
                    <div className="truncate text-[13px] text-gray-900" title={row.outcome || ''}>{row.outcome || '—'}</div>
                    <div className="truncate text-xs text-gray-500" title={row.label_created_display || ''}>
                      {formatDesktopTimestamp(row.label_created_display)}
                    </div>
                  </td>
                </tr>
              ))}
              {data && rows.length === 0 && (
                <tr><td colSpan="7" className="px-3 py-8 text-center text-sm text-gray-500">No returns match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="space-y-2 md:hidden">
          {rows.map((row) => (
            <article
              key={row.id}
              className={`rounded-lg border border-gray-200 p-3 ${
                row.state === 'unmatched' || (row.is_warranty && row.state === 'waiting') ? 'bg-amber-50/60' : 'bg-white'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-mono text-sm text-gray-900">{row.tracking_number || '—'}</div>
                  <div className="truncate text-xs text-gray-500">{row.carrier || '—'}</div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-800">{formatLabel(row.state)}</span>
                  {row.is_warranty && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">Warranty</span>
                  )}
                </div>
              </div>
              <div className="mt-2 grid gap-1 border-t border-gray-200 pt-2 text-xs">
                <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-2">
                  <span className="text-gray-500">RMA · Order</span>
                  <span className="truncate text-right text-gray-900">
                    {row.rma || '—'} · {row.customer_name
                      ? `Order ${row.order_number || '—'} · ${row.customer_name}`
                      : row.order_number || '—'}
                  </span>
                </div>
                <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-2">
                  <span className="text-gray-500">Received</span>
                  <span className="truncate text-right text-gray-900">
                    {row.received_display || '—'} · {row.receivedBy?.full_name || '—'}
                  </span>
                </div>
                <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-2">
                  <span className="text-gray-500">Due</span>
                  <span className={`truncate text-right ${row.due_status === 'overdue' ? 'text-red-700' : 'text-gray-900'}`}>
                    {row.due_display || '—'} · {formatDueStatus(row.due_status)}
                  </span>
                </div>
                <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-2">
                  <span className="text-gray-500">Processed</span>
                  <span className="truncate text-right text-gray-900">
                    {row.processed_display || '—'} · {row.time_to_process || '—'}
                  </span>
                </div>
                {row.outcome && row.outcome !== '—' && (
                  <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-2">
                    <span className="text-gray-500">Outcome</span>
                    <span className="truncate text-right text-gray-900">
                      {row.outcome}
                    </span>
                  </div>
                )}
                {row.label_created_display && row.label_created_display !== '—' && (
                  <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-2">
                    <span className="text-gray-500">Label</span>
                    <span className="truncate text-right text-gray-900">{row.label_created_display}</span>
                  </div>
                )}
              </div>
            </article>
          ))}
          {data && rows.length === 0 && (
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-8 text-center text-sm text-gray-500">
              No returns match these filters.
            </div>
          )}
        </div>
      </div>
    </ReportTemplate>
  );
};

export default ReturnsProcessingReport;
